import React, { DragEvent, KeyboardEvent, useRef, useState } from 'react';
import cn from 'class-names';

import { SVGIcon } from '@uikit/old/Icon/Icon';

import { CSV_EXTENSION, IMPORT_FILE_INPUT_ID } from '../../constants';

import './CsvDropZone.scss';

type CsvDropZonePropsType = {
  onLoadFile: (file: File) => void;
  disabled?: boolean;
};

function CsvDropZone({ onLoadFile, disabled = false }: CsvDropZonePropsType): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragged, setIsDragged] = useState(false);

  const stop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const openFilePicker = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleDragIn = (event: DragEvent<HTMLDivElement>) => {
    stop(event);
    if (!disabled && event.dataTransfer.items?.length > 0) {
      setIsDragged(true);
    }
  };

  const handleDragOut = (event: DragEvent<HTMLDivElement>) => {
    stop(event);
    setIsDragged(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    stop(event);
    setIsDragged(false);

    const file = event.dataTransfer.files?.[0];
    if (!disabled && file) {
      onLoadFile(file);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFilePicker();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onLoadFile(file);
    }
    // Let the same file be picked again after a failed launch.
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      className={cn('csv-drop-zone', {
        'csv-drop-zone--dragged': isDragged,
        'csv-drop-zone--disabled': disabled,
      })}
      onClick={openFilePicker}
      onKeyDown={handleKeyDown}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={stop}
      onDrop={handleDrop}
    >
      <div className="csv-drop-zone__icon">
        <SVGIcon icon="folderOpen" size={28} />
      </div>
      <div className="csv-drop-zone__description">
        Drag and drop the filled template here, or click to pick it
      </div>
      <span className="csv-drop-zone__button">Upload .CSV file</span>
      <input
        ref={inputRef}
        id={IMPORT_FILE_INPUT_ID}
        className="csv-drop-zone__input-field"
        data-testid="publisher-import-file-input"
        type="file"
        accept={CSV_EXTENSION}
        disabled={disabled}
        hidden
        onChange={handleInputChange}
      />
    </div>
  );
}

export default CsvDropZone;
